/**
 * Liste des utilisateurs du tenant — backoffice admin.
 *
 * Affiche une table paginée avec filtres : rôle, actif/suspendu, recherche libre.
 * Clic sur une ligne → page détail.
 *
 * Permissions : route gardée par AdminLayout (super_admin / admin / manager).
 * Le backend filtre déjà par tenant_id (req.auth.tenantId).
 *
 * Refonte R9 (mai 2026) :
 *   - En-tête de page avec LogoMark Users accent tech + Geist
 *   - Card filtres glass avec search input pl-10 + 2 selects glass
 *   - Card table glass `padded={false}` avec rows hover surface/60
 *   - Pagination en boutons R2
 *
 * Logique métier 100% préservée :
 *   - api.admin.users.list paginé (PAGE_SIZE 25) avec filtres role/actif/q
 *   - ROLE_VARIANT (super_admin→urba, admin→tech, manager→grc, agent→asso, administre→cantine)
 *   - Badges état (Supprimé / Suspendu / Verrouillé / Actif) + Badge "Email non vérifié"
 *   - canEdit = super_admin | admin → affiche colonne Actions avec lien Détail
 *   - Search Enter→submit form, reset page à 1 sur filtre
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge, Button, Input, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Search, ChevronLeft, ChevronRight, Lock, AlertCircle, Users } from 'lucide-react';
import type { AdminUserListItem, UserRole, UserService } from '@roronoa-collectivites/shared-types';

const ROLE_OPTIONS: { value: UserRole | 'all'; label: string }[] = [
  { value: 'all', label: 'Tous les rôles' },
  { value: 'super_admin', label: 'Super admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'agent', label: 'Agent' },
  { value: 'administre', label: 'Administré' },
];

const ROLE_LABEL: Record<UserRole, string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
  manager: 'Manager',
  agent: 'Agent',
  administre: 'Administré',
};

/**
 * Couleur du badge = couleur de la BRIQUE de l'agent (son `service`), pas son rôle.
 * Un manager petite enfance et un agent petite enfance ont donc le même rose fuchsia.
 * Repli sur la couleur du rôle pour les comptes sans service (admin/maire,
 * super_admin, administré).
 */
type Accent = 'tech' | 'asso' | 'urba' | 'grc' | 'cantine' | 'petite_enfance' | 'rh' | 'compta' | 'neutral';

const SERVICE_ACCENT: Record<UserService, Accent> = {
  service_technique: 'tech',
  scolaire_cantine: 'cantine',
  petite_enfance: 'petite_enfance',
  urbanisme: 'urba',
  associations: 'asso',
  grc: 'grc',
  rh: 'rh',
  comptable: 'compta',
};

const SERVICE_LABEL: Record<UserService, string> = {
  service_technique: 'Technique',
  scolaire_cantine: 'Scolaire / cantine',
  petite_enfance: 'Petite enfance',
  urbanisme: 'Urbanisme',
  associations: 'Associations',
  grc: 'Communication',
  rh: 'RH',
  comptable: 'Comptabilité',
};

/** Repli rôle → accent quand le compte n'a pas de service rattaché. */
const ROLE_ACCENT_FALLBACK: Record<UserRole, Accent> = {
  super_admin: 'urba',
  admin: 'tech',
  manager: 'neutral',
  agent: 'neutral',
  administre: 'neutral',
};

/** Accent effectif d'un utilisateur : brique si présente, sinon repli rôle. */
function userAccent(u: { role: UserRole; service: UserService | null }): Accent {
  if (u.service) return SERVICE_ACCENT[u.service];
  return ROLE_ACCENT_FALLBACK[u.role];
}

/** Libellé du badge : « Rôle — Secteur » (ex. « Agent — Petite enfance »). */
function userBadgeLabel(u: { role: UserRole; service: UserService | null }): string {
  const r = ROLE_LABEL[u.role];
  if (u.service) return `${r} · ${SERVICE_LABEL[u.service]}`;
  return r;
}

type SortMode = 'recent' | 'secteur' | 'alpha';

const PAGE_SIZE = 25;

// Styles partagés
const SELECT_CLS = 'h-10 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

export function UsersListPage() {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState<AdminUserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [actifFilter, setActifFilter] = useState<'all' | 'actif' | 'suspendu'>('all');
  const [sortMode, setSortMode] = useState<SortMode>('recent');
  const [q, setQ] = useState('');
  const [qApplied, setQApplied] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.users.list({
        page,
        pageSize: PAGE_SIZE,
        role: roleFilter === 'all' ? undefined : roleFilter,
        actif: actifFilter === 'all' ? undefined : actifFilter === 'actif',
        q: qApplied || undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.payload.error || 'Erreur lors du chargement');
      } else {
        setError('Erreur réseau');
      }
    } finally {
      setLoading(false);
    }
  }, [page, roleFilter, actifFilter, qApplied]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setQApplied(q);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canEdit = currentUser && (currentUser.role === 'super_admin' || currentUser.role === 'admin');

  // Tri appliqué à la page courante (la pagination reste côté serveur).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    if (sortMode === 'alpha') {
      copy.sort((a, b) =>
        `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr', { sensitivity: 'base' })
      );
    } else if (sortMode === 'secteur') {
      copy.sort((a, b) => {
        const sa = a.service ? SERVICE_LABEL[a.service] : '\uffff'; // sans service → en fin
        const sb = b.service ? SERVICE_LABEL[b.service] : '\uffff';
        const cmp = sa.localeCompare(sb, 'fr', { sensitivity: 'base' });
        if (cmp !== 0) return cmp;
        return `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr', { sensitivity: 'base' });
      });
    }
    return copy;
  }, [items, sortMode]);

  return (
    <section aria-labelledby="users-title" className="animate-fade-in">
      {/* En-tête de page : LogoMark + titre */}
      <header className="mb-6 flex items-center gap-3">
        <LogoMark icon={Users} accent="tech" size="lg" />
        <div>
          <SectionEyebrow as="div">Administration</SectionEyebrow>
          <h1
            id="users-title"
            className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl"
          >
            Utilisateurs
          </h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            {total} utilisateur{total > 1 ? 's' : ''} dans la collectivité
          </p>
        </div>
      </header>

      {/* Filtres en Card glass */}
      <Card variant="glass" padded className="mb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <form onSubmit={applySearch} className="flex flex-1 gap-2">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim"
                aria-hidden="true"
              />
              <Input
                type="search"
                placeholder="Rechercher (email, prénom, nom)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Rechercher un utilisateur"
                className="pl-10"
              />
            </div>
            <Button type="submit" variant="primary" size="md">
              Rechercher
            </Button>
          </form>
          <div className="flex flex-wrap gap-2">
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value as UserRole | 'all');
                setPage(1);
              }}
              aria-label="Filtrer par rôle"
              className={SELECT_CLS}
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={actifFilter}
              onChange={(e) => {
                setActifFilter(e.target.value as 'all' | 'actif' | 'suspendu');
                setPage(1);
              }}
              aria-label="Filtrer par état"
              className={SELECT_CLS}
            >
              <option value="all">Tous les états</option>
              <option value="actif">Actifs uniquement</option>
              <option value="suspendu">Suspendus uniquement</option>
            </select>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as SortMode)}
              aria-label="Trier"
              className={SELECT_CLS}
            >
              <option value="recent">Tri : récents d'abord</option>
              <option value="secteur">Tri : par secteur</option>
              <option value="alpha">Tri : alphabétique</option>
            </select>
          </div>
        </div>
      </Card>

      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Table en Card glass */}
      <Card variant="glass" padded={false}>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-border bg-surface/40 backdrop-blur-glass">
              <tr className="text-left">
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Identité</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Rôle</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">État</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Dernière connexion</th>
                {canEdit && (
                  <th className="px-4 py-3 text-right text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="text-sm">
              {loading ? (
                <tr>
                  <td colSpan={canEdit ? 5 : 4} className="px-4 py-12 text-center text-ink-muted">
                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-tech border-t-transparent" role="status" />
                    <p className="mt-3">Chargement…</p>
                  </td>
                </tr>
              ) : sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 5 : 4} className="px-4 py-12 text-center text-ink-muted">
                    Aucun utilisateur ne correspond aux critères.
                  </td>
                </tr>
              ) : (
                sortedItems.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-border last:border-b-0 transition-colors hover:bg-surface/60"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-ink">
                        {u.prenom} {u.nom}
                      </div>
                      <div className="text-xs text-ink-muted">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={userAccent(u)} size="sm">
                        {userBadgeLabel(u)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {u.deletedAt ? (
                          <Badge variant="neutral" size="sm">
                            Supprimé
                          </Badge>
                        ) : !u.actif ? (
                          <Badge variant="warning" size="sm">
                            Suspendu
                          </Badge>
                        ) : u.lockedUntil && new Date(u.lockedUntil) > new Date() ? (
                          <Badge variant="warning" size="sm">
                            <Lock className="h-3 w-3" aria-hidden="true" /> Verrouillé
                          </Badge>
                        ) : (
                          <Badge variant="success" size="sm">
                            Actif
                          </Badge>
                        )}
                        {!u.emailVerified && (
                          <Badge variant="neutral" size="sm">
                            <AlertCircle className="h-3 w-3" aria-hidden="true" /> Email non vérifié
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-muted">
                      {u.lastLogin ? new Date(u.lastLogin).toLocaleString('fr-FR') : '—'}
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/utilisateurs/${u.id}`}
                          className="inline-flex h-9 items-center rounded-r-md px-3 text-sm font-medium text-tech-fg transition-colors hover:bg-tech/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                        >
                          Détail
                        </Link>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3 text-sm text-ink-muted">
            <span className="tabular-nums">
              Page {page} sur {totalPages} · {total} résultat{total > 1 ? 's' : ''}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Page précédente"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Page suivante"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </section>
  );
}
